import { Column, Columns, Span, Tag, Tags } from "trunx";
import { decimalPlaces, useLocaleContext } from "../../contexts/LocaleContext";

import { observer } from "mobx-react-lite";
import { splitEvery } from "ramda";
import convert from "../../convert";
import { toIntensity } from "../../models/PrecipSensor";
import { ISelectedPrecipSensor } from "../../models/SelectedSensor";

const PrecipDataTable = ({ selected }: { selected: ISelectedPrecipSensor }) => {
  const { sensor, display } = selected;

  const {
    dict: {
      intervals: dict,
      panel: {
        sensors: {
          info: { precip: precipTitle, intensity: intensityTitle },
        },
      },
    },
    units: {
      precip: { total: toDepthUnit, intensity: toIntensityUnit },
    },
  } = useLocaleContext();

  const title = display.measure === "total" ? precipTitle : intensityTitle;

  interface DataTagProps {
    interval: string;
    value: number | null;
  }

  let tags: DataTagProps[] = [];
  for (const [interval, depth] of sensor.depths.entries()) {
    tags.push({
      interval,
      value:
        display.measure === "total"
          ? depth
          : depth && toIntensity(depth, interval),
    });
  }

  const fromUnit =
    display.measure === "total" ? sensor.totalUnit : sensor.intensityUnit;
  const toUnit = display.measure === "total" ? toDepthUnit : toIntensityUnit;

  const DataTag = observer(({ interval, value }: DataTagProps) => {
    const value_ =
      value !== null
        ? convert(value)
            .from(fromUnit)
            .to(toUnit)
            .toFixed(decimalPlaces(toUnit))
        : "-";
    const label = dict.durationLabel[interval];
    const isDisplaying = interval === display.interval;
    return (
      <Tags
        className="is-precip is-justify-content-center"
        key={interval}
        hasAddons
      >
        <Tag key="label" bulma={isDisplaying ? ["is-info"] : []}>
          {label}
        </Tag>
        <Tag key="value" bulma={!!value && value > 0 ? ["is-primary"] : []}>
          {value_}
        </Tag>
      </Tags>
    );
  });

  interface DataColumnProps {
    idx: number;
    tags: DataTagProps[];
  }
  const DataColumn = ({ idx, tags }: DataColumnProps) => (
    <Column key={idx.toString()}>
      {tags.map((tag) => (
        <DataTag key={tag.interval} {...tag} />
      ))}
    </Column>
  );

  return (
    <div id="sensor-data-table">
      <p className="subtitle">
        {title}&nbsp;
        <Span bulma="is-size-6">({toUnit})</Span>
      </p>
      <Columns bulma="is-mobile">
        {splitEvery(4, tags).map((c, idx) => (
          <DataColumn tags={c} key={idx} idx={idx} />
        ))}
      </Columns>
    </div>
  );
};

export default observer(PrecipDataTable);
